import { DateTime } from "luxon";

/*
 * The venue-local calendar the prompted day and time pickers count in.
 *
 * Every function here reads and writes whole days as `yyyy-MM-dd` in the
 * facility's zone, the same shape the booking mutations already use, so a
 * picked slot survives the trip to the server unchanged. The instants stay
 * ISO; only the days are strings.
 */

const DAY = "yyyy-MM-dd";

const MONTH = "yyyy-MM";

function zoned(iso, tz) {
  return DateTime.fromISO(iso, tz ? { zone: tz } : undefined);
}

export function nowIso(tz) {
  return (tz ? DateTime.now().setZone(tz) : DateTime.now()).toISO();
}

/** The venue-local day an instant falls on. */
export function dayOf(iso, tz) {
  if (!iso) return null;

  const dt = zoned(iso, tz);

  return dt.isValid ? dt.toFormat(DAY) : null;
}

export function addDays(day, delta, tz) {
  const dt = DateTime.fromISO(day, tz ? { zone: tz } : undefined);

  return dt.isValid ? dt.plus({ days: delta }).toFormat(DAY) : day;
}

export function monthOf(day) {
  return day ? day.slice(0, 7) : null;
}

export function addMonths(month, delta) {
  const dt = DateTime.fromISO(`${month}-01`);

  return dt.isValid ? dt.plus({ months: delta }).toFormat(MONTH) : month;
}

/**
 * Today, Tomorrow, then the weekday and date - what a chip says.
 */
export function dayLabel({ day, tz, now }) {
  if (!day) return "";

  const today = dayOf(now ?? nowIso(tz), tz);

  if (day === today) return "Today";
  if (day === addDays(today, 1, tz)) return "Tomorrow";

  const dt = DateTime.fromISO(day, tz ? { zone: tz } : undefined);

  return dt.isValid ? dt.toFormat("ccc d LLL") : day;
}

/** The weekday and date of a day, however near it is: "Fri 12 Sep". */
export function dateLabel({ day, tz }) {
  const dt = DateTime.fromISO(day, tz ? { zone: tz } : undefined);

  return dt.isValid ? dt.toFormat("ccc d LLL") : day;
}

export function clockLabel(iso, tz) {
  const dt = zoned(iso, tz);

  return dt.isValid ? dt.toFormat("HH:mm") : "";
}

/** "Tomorrow · 09:30", the line under a picker that names what is chosen. */
export function whenText({ iso, tz, now }) {
  if (!iso) return "Not set";

  return `${dayLabel({ day: dayOf(iso, tz), tz, now })} · ${clockLabel(iso, tz)}`;
}

/** The days a picker offers as chips before the calendar takes over. */
export function dayChoices({ from, count, tz }) {
  return Array.from({ length: count }, (_, index) => addDays(from, index, tz));
}

/** One slot per step through the day's open hours, as venue-local instants. */
export function timeSlots({ day, tz, stepMinutes, hours }) {
  const start = DateTime.fromISO(day, tz ? { zone: tz } : undefined);
  if (!start.isValid) return [];

  const slots = [];

  for (let minute = hours.from * 60; minute <= hours.to * 60; minute += stepMinutes) {
    const dt = start.plus({ minutes: minute });

    slots.push({ iso: dt.toISO(), label: dt.toFormat("HH:mm") });
  }

  return slots;
}

/** A `HH:mm` typed into the manual field -> an instant on that day. */
export function isoAt({ day, clock, tz }) {
  const dt = DateTime.fromISO(`${day}T${clock}`, tz ? { zone: tz } : undefined);

  return dt.isValid ? dt.toISO() : null;
}

/**
 * A month laid out the way a calendar grid reads it: Monday first, with the
 * leading blanks the first row needs.
 */
export function monthGrid({ month, tz, now }) {
  const first = DateTime.fromISO(`${month}-01`, tz ? { zone: tz } : undefined);
  if (!first.isValid) return { label: month, leading: 0, days: [] };

  const today = dayOf(now ?? nowIso(tz), tz);

  const days = Array.from({ length: first.daysInMonth }, (_, index) => {
    const dt = first.plus({ days: index });
    const day = dt.toFormat(DAY);

    return { day, number: index + 1, past: day < today, today: day === today };
  });

  return {
    label: first.toFormat("LLLL yyyy"),
    /* Luxon's weekday is 1 (Monday) to 7 (Sunday), which is already the order
       the grid's columns run in. */
    leading: first.weekday - 1,
    days,
  };
}
